import React, { useState, useRef, useEffect, useCallback } from 'react';
import './InlineEdit.css';

export interface InlineEditProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSave'> {
  /** Current value to display */
  value: string;
  /** Called when the user saves the edited value */
  onSave: (newValue: string) => void;
  /** Placeholder text when value is empty */
  placeholder?: string;
  /** Accessible label for the input */
  label?: string;
  /** Disable editing */
  disabled?: boolean;
  /** Maximum input length */
  maxLength?: number;
}

export const InlineEdit: React.FC<InlineEditProps> = ({
  value,
  onSave,
  placeholder = '',
  label,
  disabled = false,
  maxLength,
  className = '',
  ...rest
}) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEditing = useCallback(() => {
    setEditValue(value);
    setEditing(true);
  }, [value]);

  const handleSave = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== value) {
      onSave(trimmed);
    }
    setEditing(false);
  }, [editValue, value, onSave]);

  const handleCancel = useCallback(() => {
    setEditValue(value);
    setEditing(false);
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSave();
      } else if (e.key === 'Escape') {
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  const classes = [
    'hi-ui-inline-edit',
    editing ? 'hi-ui-inline-edit--editing' : '',
    disabled ? 'hi-ui-inline-edit--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} {...rest}>
      {editing ? (
        <div className="hi-ui-inline-edit__form">
          <input
            ref={inputRef}
            type="text"
            className="hi-ui-inline-edit__input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            maxLength={maxLength}
            aria-label={label}
          />
          <button
            type="button"
            className="hi-ui-inline-edit__save"
            onClick={handleSave}
            aria-label="Save"
          >
            <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
            </svg>
          </button>
          <button
            type="button"
            className="hi-ui-inline-edit__cancel"
            onClick={handleCancel}
            aria-label="Cancel"
          >
            <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M4.47 4.47a.75.75 0 011.06 0L8 6.94l2.47-2.47a.75.75 0 111.06 1.06L9.06 8l2.47 2.47a.75.75 0 11-1.06 1.06L8 9.06l-2.47 2.47a.75.75 0 01-1.06-1.06L6.94 8 4.47 5.53a.75.75 0 010-1.06z" />
            </svg>
          </button>
        </div>
      ) : (
        <div className="hi-ui-inline-edit__display">
          <span className="hi-ui-inline-edit__value">
            {value || <span className="hi-ui-inline-edit__placeholder">{placeholder}</span>}
          </span>
          {!disabled && (
            <button
              type="button"
              className="hi-ui-inline-edit__edit"
              onClick={startEditing}
              aria-label={label ? `Edit ${label}` : 'Edit'}
            >
              <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M11.013 1.427a1.75 1.75 0 012.474 0l1.086 1.086a1.75 1.75 0 010 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 01-.927-.928l.929-3.25a1.75 1.75 0 01.445-.758l8.61-8.61zm1.414 1.06a.25.25 0 00-.354 0L3.463 11.1a.25.25 0 00-.064.108l-.564 1.97 1.971-.564a.25.25 0 00.108-.064l8.61-8.61a.25.25 0 000-.354l-1.086-1.086z" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
